import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  if (!form) return <p className="text-[#9B7E73]">Chargement...</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setZone = (i, k, v) => set("delivery_zones", form.delivery_zones.map((z, idx) => idx === i ? { ...z, [k]: k === "frais" ? parseInt(v) || 0 : v } : z));
  const addZone = () => set("delivery_zones", [...form.delivery_zones, { nom: "", frais: 0 }]);
  const removeZone = (i) => set("delivery_zones", form.delivery_zones.filter((_, idx) => idx !== i));

  const save = async () => {
    try {
      const payload = {
        ...form,
        alert_emails: Array.isArray(form.alert_emails)
          ? form.alert_emails
          : String(form.alert_emails || "").split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/admin/settings", payload);
      toast.success("Paramètres enregistrés");
      refresh();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="mb-6 font-serif text-3xl font-medium text-[#2C1A14]">Paramètres</h1>

      <div className="space-y-6">
        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-2 font-serif text-xl text-[#2C1A14]">Modes de paiement</h2>
          <p className="mb-4 text-xs text-[#9B7E73]">Activez les moyens de paiement proposés au client et expliquez leur fonctionnement. Pour Wave/Orange Money, indiquez le numéro à créditer.</p>
          <div className="space-y-4">
            {(form.payment_methods || []).map((m, i) => (
              <div key={m.id} data-testid={`pm-${m.id}`} className="rounded-lg border border-[#E8D5CE] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Input
                      value={m.label}
                      onChange={(e) => set("payment_methods", form.payment_methods.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))}
                      className="h-8 w-56 bg-white font-medium"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${m.enabled ? "text-green-700" : "text-[#9B7E73]"}`}>{m.enabled ? "Activé" : "Désactivé"}</span>
                    <Switch
                      checked={!!m.enabled}
                      onCheckedChange={(v) => set("payment_methods", form.payment_methods.map((x, idx) => idx === i ? { ...x, enabled: v } : x))}
                      data-testid={`pm-toggle-${m.id}`}
                    />
                  </div>
                </div>
                {m.id !== "livraison" && (
                  <div className="mt-3">
                    <Label className="text-xs">Numéro à créditer</Label>
                    <Input
                      value={m.numero || ""}
                      onChange={(e) => set("payment_methods", form.payment_methods.map((x, idx) => idx === i ? { ...x, numero: e.target.value } : x))}
                      data-testid={`pm-numero-${m.id}`}
                      className="mt-1 bg-white"
                    />
                  </div>
                )}
                <div className="mt-3">
                  <Label className="text-xs">Explication affichée au client</Label>
                  <Textarea
                    value={m.instructions || ""}
                    onChange={(e) => set("payment_methods", form.payment_methods.map((x, idx) => idx === i ? { ...x, instructions: e.target.value } : x))}
                    data-testid={`pm-instructions-${m.id}`}
                    rows={2}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Contact</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div><Label>Numéro WhatsApp Business</Label><Input data-testid="settings-whatsapp" value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>Nom marchand (affiché)</Label><Input value={form.wave_name} onChange={(e) => set("wave_name", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>Email de contact</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>Adresse</Label><Input value={form.contact_adresse} onChange={(e) => set("contact_adresse", e.target.value)} className="mt-1 bg-white" /></div>
            <div className="sm:col-span-2">
              <Label>Emails d'alerte « nouvelle commande »</Label>
              <Textarea
                data-testid="settings-alert-emails"
                value={Array.isArray(form.alert_emails) ? form.alert_emails.join(", ") : (form.alert_emails || "")}
                onChange={(e) => set("alert_emails", e.target.value)}
                placeholder="email1@exemple.com, email2@exemple.com"
                className="mt-1 bg-white"
              />
              <p className="mt-1 text-xs text-[#9B7E73]">Séparez les adresses par une virgule. Ces personnes recevront un email à chaque nouvelle commande.</p>
            </div>
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-serif text-xl text-[#2C1A14]">Frais de livraison par zone (Yango)</h2>
            <Button onClick={addZone} data-testid="add-zone" variant="outline" className="border-[#E8D5CE]"><Plus className="mr-1 h-4 w-4" /> Zone</Button>
          </div>
          <div className="space-y-2">
            {form.delivery_zones.map((z, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input value={z.nom} onChange={(e) => setZone(i, "nom", e.target.value)} data-testid={`zone-nom-${i}`} placeholder="Commune" className="flex-1 bg-white" />
                <Input type="number" value={z.frais} onChange={(e) => setZone(i, "frais", e.target.value)} data-testid={`zone-frais-${i}`} placeholder="Frais" className="w-32 bg-white" />
                <span className="text-xs text-[#9B7E73]">FCFA</span>
                <button onClick={() => removeZone(i)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Informations boutique</h2>
          <div className="space-y-4">
            <div><Label>Info livraison (footer)</Label><Textarea value={form.livraison_info} onChange={(e) => set("livraison_info", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>Politique de retour</Label><Textarea value={form.return_policy} onChange={(e) => set("return_policy", e.target.value)} className="mt-1 bg-white" /></div>
            <div><Label>CGV / Mentions légales</Label><Textarea value={form.cgv} onChange={(e) => set("cgv", e.target.value)} className="mt-1 bg-white" /></div>
          </div>
        </section>

        <section className="rounded-xl bg-white p-6 ring-1 ring-[#E8D5CE]">
          <h2 className="mb-4 font-serif text-xl text-[#2C1A14]">Réseaux sociaux</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div><Label>Facebook</Label><Input data-testid="settings-facebook" value={form.social?.facebook || ""} onChange={(e) => set("social", { ...form.social, facebook: e.target.value })} placeholder="https://facebook.com/votrepage" className="mt-1 bg-white" /></div>
            <div><Label>Instagram</Label><Input data-testid="settings-instagram" value={form.social?.instagram || ""} onChange={(e) => set("social", { ...form.social, instagram: e.target.value })} placeholder="https://instagram.com/votrecompte" className="mt-1 bg-white" /></div>
            <div><Label>TikTok</Label><Input value={form.social?.tiktok || ""} onChange={(e) => set("social", { ...form.social, tiktok: e.target.value })} className="mt-1 bg-white" /></div>
          </div>
        </section>

        <Button onClick={save} data-testid="save-settings" className="rounded-full bg-[#D76E4D] px-8 py-6 text-white hover:bg-[#C05A3A]">Enregistrer les paramètres</Button>
      </div>
    </div>
  );
}
